<template>
  <v-row align="start" id="addOrderContainer" data-testid="add-order-form">
    <v-col sm="12" md="6" class="add-order-component">
      <cart-builder
        :storeID="storeID"
        :campaign="campaign"
        :disabled="placingOrder"
      />
    </v-col>
    <v-col sm="12" md="6" class="add-order-component">
      <cart
        :storeID="storeID"
        :campaign="campaign"
        :disabled="placingOrder"
        :clientDepartmentId="clientDepartmentId"
      />
    </v-col>
    <v-col sm="12" class="add-order-component">
      <shopper-details
        ref="shopperDetails"
        v-model:clientDepartmentId="clientDepartmentId"
        :storeID="storeID"
        :campaign="campaign"
        :disabled="placingOrder"
        @addressValidated="addOrder"
      />
    </v-col>
    <v-col
      cols="12"
      class="add-order-component"
      v-if="
        campaign.payment_method == 'Individual CC Payment' &&
          displayCreditCardReader
      "
    >
      <credit-card-reader
        ref="credit-card-reader"
        :storeID="storeID"
        :campaign="campaign"
        :disabled="placingOrder"
      />
    </v-col>
    <v-col cols="12" class="mt-2">
      <v-btn
        @click="placeOrderClicked"
        class="full-width-fab"
        rounded="pill"
        size="large"
        color="primary"
        :disabled="placingOrder || placeOrderButtonDisabled"
        data-testid="add-order-place-order"
      >
        <v-progress-circular
          v-if="placingOrder"
          :indeterminate="true"
          size="25"
          width="3"
          color="white"
          class="loading-wheel"
        />
        <v-icon v-else color="white" class="mr-2 extended-fab-icon"
          >shopping_cart</v-icon
        >

        <div style="color: white" v-if="!placingOrder">Place Order</div>
      </v-btn>
    </v-col>
    <re-order
      ref="reOrderDialog"
      @goBack="exitAddOrder"
      @addAnotherOrder="resetView"
    />
  </v-row>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import store from "@/store";
import currency from "currency.js";
import helpers from "@/mixins/helpers";
import CartBuilder from "./CartBuilder.vue";
import ShopperDetails, { NO_LOCATION_OPTION } from "./ShopperDetails.vue";
import Cart from "./Cart.vue";
import { mapGetters } from "vuex";
import CreditCardReader from "./CreditCardReader.vue";
import { loadStripe } from "@stripe/stripe-js";
import StripeEvent from "@/models/StripeEvent";
import { chargeOrder, saveCardForOrder } from "@/services/orderCardPayment";
import ReOrder from "./ReOrder.vue";
import { cartStoreArgs } from "@/router/storefrontStoreId";
import { storeRatesLive } from "@/services/shippingService";
import { storeResolvesDestination } from "@/utils/campaign";
import { requestCheckoutQuote } from "@/components/Storefront/checkoutQuoteClient";

// The typed address as checkout reads it: "street, [suite, ]city, region,
// zip, country" (`account._parse_shopper_location`), or "" when there is no
// complete address.
function shopperLocationField(location) {
  if (!location || typeof location !== "object") return "";
  const parts = [
    location.street_and_number,
    location.suite_unit_etc,
    location.city,
    location.region,
    location.zip_code,
    location.country,
  ].map((part) => (typeof part === "string" ? part.trim() : ""));
  const [street, suite, city, region, zip, country] = parts;
  if (![street, city, region, zip, country].every(Boolean)) return "";
  return [street, suite, city, region, zip, country].filter(Boolean).join(", ");
}

// How long addOrder waits for the cart's live shipping quote after the
// address is validated, before it tells the user instead.
const SHIPPING_QUOTE_WAIT_MS = 15000;

export default {
  components: {
    CartBuilder,
    Cart,
    ShopperDetails,
    CreditCardReader,
    ReOrder,
  },
  mixins: [helpers],

  props: {
    storeID: {
      type: [Number, String],
    },
    campaign: {
      type: Object,
      default: null,
    },
    displayCreditCardReader: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      creditCardInfo: {},
      stripe: null,
      placingOrder: false,
      // The typed department pick from ShopperDetails; null on a store
      // that needs none (0 or 1 department, resolved server-side).
      clientDepartmentId: null,
    };
  },

  computed: {
    ...mapGetters([
      "getShopperShippingAvailable",
      "getShopperShippingQuoteId",
      "getShopperCoupon",
      "getCheckoutFieldsValues",
      "getIsCompanyLocation",
      "getShopperLocation",
      "getShopperLocationValidated",
      "getShopperDetailsValid",
      "getCartValid",
      "getCartLoading",
    ]),

    // The cart needed a live shipping quote and has none. The order must not
    // go through at a $0 the cart never priced (#2882); the server refuses
    // shipping_rate_available: false too.
    shippingRateUnavailable() {
      return Boolean(
        this.campaign &&
          this.campaign.shipping_cost_enabled &&
          this.getShopperShippingAvailable === false
      );
    },

    // A live-rate store charges the SERVER's stored quote, and checkout
    // refuses (409) an order without one. Jake (2026-10-26): Place Order is
    // gated on live-rate, no quote yet -- a pick-up location included (Bug
    // Intake #548: free pick-up is dropped for MVP). The cart asks for
    // the quote as soon as it knows where the order ships (the store's
    // destination, or a complete typed address), so there is never a $0
    // shipping line to place an order on.
    shippingQuoteMissing() {
      return Boolean(
        this.campaign &&
          storeRatesLive(this.campaign) &&
          !this.getShopperShippingQuoteId
      );
    },

    placeOrderButtonDisabled() {
      return (
        !this.getShopperDetailsValid ||
        !this.getCartValid ||
        this.getCartLoading ||
        this.shippingRateUnavailable ||
        this.shippingQuoteMissing
      );
    },
  },

  unmounted() {
    this.creditCardInfo = null;
    this.stripe = null;
    this.clearCart();
  },

  mounted() {
    this.$store.commit("setUsingCreditCard", this.displayCreditCardReader);
    this.clearCart();
    this.$store.commit("setProductsLoaded", false);
  },

  watch: {
    displayCreditCardReader: {
      handler() {
        this.$store.commit("setUsingCreditCard", this.displayCreditCardReader);
      },
    },
  },

  methods: {
    async clearCart() {
      this.$store.commit("setCart", []);
      try {
        await this.$axios.post(
          `/accounts/${this.$store.state.account._jv.id}/delete_cart`,
          {
            meta: {
              method: "delete_cart",
              args: cartStoreArgs(this.storeID),
            },
          }
        );
      } catch (err) {
        this.$store.dispatch("raiseError", err);
      }
    },
    exitAddOrder() {
      this.$emit("exit");
      this.clearCart();
    },

    async addOrder() {
      // The address-validated path reaches here without going through the
      // disabled button, so the guard lives here too.
      if (this.shippingRateUnavailable) {
        this.$store.dispatch(
          "raiseError",
          "A shipping rate is not available for this address yet, so the order cannot be placed. Check the address or try again."
        );
        return;
      }
      this.placingOrder = true;
      try {
        // The address-validated path commits the (possibly corrected)
        // address and emits in the same tick. Let the cart see it first: it
        // drops the previous address's quote id, so the wait below is for a
        // quote of THIS address, never a stale one.
        await this.$nextTick();
        // Wait for the cart's live quote rather than place a $0 line.
        if (!(await this.ensureShippingQuote())) {
          this.$store.dispatch(
            "raiseError",
            "The shipping cost has not been calculated yet, so the order cannot be placed. Wait a moment and try again."
          );
          this.placingOrder = false;
          return;
        }
        // Every figure posted below -- and the card amount -- comes from the
        // server's quote, computed by the functions checkout recomputes
        // with. Cart.vue's grand total is a DISPLAY figure: it has no
        // free-item discount or platform fee, and folds tax and shipping
        // into what must be the items subtotal.
        const quote = await this.fetchCheckoutQuote();
        if (!quote) {
          return;
        }
        if (
          this.campaign.payment_method == "Individual CC Payment" &&
          this.displayCreditCardReader
        ) {
          await this.payNow(quote);
        } else {
          await this.placeOrder(quote);
        }
      } catch (err) {
        console.log(describeError(err));
      }
      this.placingOrder = false;
    },

    // Resolves true once no live quote is outstanding: immediately when none
    // is needed or one is held, else when the cart delivers one. Resolves
    // false on the rate-unavailable flag or the timeout.
    ensureShippingQuote() {
      if (!this.shippingQuoteMissing) {
        return Promise.resolve(true);
      }
      const waitMs = this.shippingQuoteWaitMs ?? SHIPPING_QUOTE_WAIT_MS;
      return new Promise((resolve) => {
        let unwatch = () => {};
        const finish = (ok) => {
          clearTimeout(timer);
          unwatch();
          resolve(ok);
        };
        const timer = setTimeout(() => finish(false), waitMs);
        unwatch = this.$store.watch(
          () => [
            this.$store.getters.getShopperShippingQuoteId,
            this.$store.getters.getShopperShippingAvailable,
          ],
          ([quoteId, available]) => {
            if (quoteId) finish(true);
            else if (available === false) finish(false);
          }
        );
      });
    },

    async placeOrderClicked() {
      if (
        !this.getIsCompanyLocation &&
        !this.getShopperLocationValidated &&
        this.$refs.shopperDetails.displayShopperLocation
      ) {
        this.$refs.shopperDetails.validateAddress();
      } else {
        this.addOrder();
      }
    },

    resetView() {
      this.clearCart();
      this.$refs.shopperDetails.clearShopperDetails();
      var element = document.getElementById("addOrderContainer");
      const yOffset = -200;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
    },

    showNewOrderDialog() {
      this.$refs.reOrderDialog.show();
    },

    // The typed location checkout taxes against. A company / department
    // store's order has none: the server resolves its destination itself.
    buyerLocation() {
      if (
        !this.getShopperLocation ||
        storeResolvesDestination(this.campaign)
      ) {
        return null;
      }
      return {
        street_and_number: this.getShopperLocation.street_and_number,
        city: this.getShopperLocation.city,
        zip_code: this.getShopperLocation.zip_code,
        region: this.getShopperLocation.region,
        country: this.getShopperLocation.country,
        isCompanyLocation: this.getIsCompanyLocation,
      };
    },

    // The money `checkout` will recompute for this account's cart, from the
    // server (`checkout_quote`, read-only). Returns null on failure, after
    // reporting it.
    async fetchCheckoutQuote() {
      let result;
      try {
        result = await requestCheckoutQuote(
          this.$axios,
          this.$store.state.account._jv.id,
          this.storeID,
          {
            client_department_id: this.clientDepartmentId ?? null,
            buyer_location: this.buyerLocation(),
            // No shipping figure: on a live-rate store the quote prices the
            // server's STORED amount for this id, the one checkout will
            // charge; a flat store is priced from its own config (#549).
            shipping_quote_id: this.getShopperShippingQuoteId,
            coupon_code: this.getShopperCoupon || null,
            // On an email-domain store the allowance belongs to an email
            // address (card #596 follow-up): name the shopper's, so the
            // quote draws the allowance checkout will.
            shopper_email:
              (this.getCheckoutFieldsValues || {})["Company Email"] || null,
          }
        );
      } catch (err) {
        this.$store.dispatch("raiseError", err);
        return null;
      }
      // A live-rate order the server has no quote for, or a flat store whose
      // config cannot price the cart (#549), is not priced; checkout would
      // refuse it (409). Never place it on a $0 line.
      if (!result || result.shipping_quote_required || result.shipping_unpriced) {
        this.$store.dispatch(
          "raiseError",
          "The shipping cost has not been calculated yet, so the order cannot be placed. Wait a moment and try again."
        );
        return null;
      }
      return result;
    },

    async payNow(quote) {
      // The storefront's two-step card flow (one server mechanism for both
      // surfaces): pre-create the order, save the card (batched order) or
      // charge it against the order at the SERVER's amount (unbatched), then
      // finalize with the proof the server recorded. See
      // services/orderCardPayment.js. Nothing here computes a card amount.
      this.creditCardInfo = this.$refs[
        "credit-card-reader"
      ].getCreditCardInfo();
      this.loading = true;
      try {
        const fullName =
          this.getCheckoutFieldsValues["First Name"] +
          " " +
          this.getCheckoutFieldsValues["Last Name"];
        const description =
          "Store: " +
          this.campaign.name.replaceAll("/", "-") +
          " Name: " +
          fullName.replaceAll("/", "-");
        const card = {
          axios: this.$axios,
          stripe: this.creditCardInfo.stripe,
          setupStripe: this.creditCardInfo.setupStripe,
          cardElement: this.creditCardInfo.cardNumber,
          name: fullName,
        };

        const checkoutFields = await this.prepareCheckoutFields();
        if (!checkoutFields) {
          return null;
        }

        // 1. Pre-create. A refusal here touches no card.
        let pending;
        try {
          const response = await this.$axios.post(
            `/accounts/${this.$store.state.account._jv.id}/create_pending_order`,
            {
              meta: {
                method: "create_pending_order",
                args: this.checkoutArgs(quote, checkoutFields),
              },
            }
          );
          pending = response.data.meta.result;
        } catch (err) {
          this.$store.dispatch("raiseError", err);
          return null;
        }
        if (!pending || !pending.id) {
          return null;
        }

        // 2. Save or charge the card against THAT order. On a failure the
        //    pre-created order is left payment_pending (or batched with no
        //    card); the pending-order sweep cancels it.
        let proof;
        if (pending.deferred_payment) {
          const saved = await saveCardForOrder({ ...card, orderId: pending.id });
          if (saved.error) {
            this.$store.dispatch("raiseError", saved.error);
            return null;
          }
          proof = { setup_intent_id: saved.setupIntent.id };
        } else {
          const charged = await chargeOrder({
            ...card,
            orderId: pending.id,
            description,
            taxCollectedCents: currency(quote.sales_tax).intValue,
          });
          if (charged.error) {
            this.$store.dispatch("raiseError", charged.error);
            return null;
          }
          proof = { payment_intent_id: charged.paymentIntent.id };
          const stripeEvent = StripeEvent.generateEmpty();
          stripeEvent.amount_charged = currency(quote.card_charge).intValue;
          stripeEvent.app_fee = quote.application_fee;
          stripeEvent.processing_fee = quote.processing_fee;
          stripeEvent.order_id = pending.id;
          stripeEvent.stripe_charge_id = charged.paymentIntent.id;
          stripeEvent.status = "charge_successful";
          await this.pushStripeEvent(stripeEvent);
        }

        // 3. Finalize: the server verifies the proof against its own record.
        return await this.submitCheckout(
          { ...this.checkoutArgs(quote, checkoutFields), order_id: pending.id, ...proof }
        );
      } finally {
        this.loading = false;
      }
    },

    async pushStripeEvent(event) {
      let response;
      try {
        response = await this.$axios.post(`/stripe/createStripeEvent`, {
          event: event,
        });
      } catch (err) {
        this.$store.dispatch("raiseError", err);
        console.log(describeError(err));
      }
    },

    formatLocation(location) {
      if (location) {
        const formattedLocation = {};
        formattedLocation.street_and_number = location.street_and_number;
        formattedLocation.city = location.city;
        formattedLocation.zip_code = location.zip_code;
        formattedLocation.region = location.region;
        formattedLocation.country = location.country;
        formattedLocation.suite_unit_etc = location.suite_unit_etc
          ? location.suite_unit_etc
          : "";
        formattedLocation.isCompanyLocation = location.isCompanyLocation;
        return formattedLocation;
      }
    },

    // The checkout_fields to post. Placing an order no longer writes the
    // store's settings or its locations (the old `update_campaign` POST and
    // locations PATCHes; Jake, 2026-10-26): an order is not a store edit.
    //
    // Ship-to rides in the fields the way the storefront sends it: a
    // shopper-address store's typed address as "Shopper Location"; a
    // company / department store's destination is the server's own.
    //
    // "Location" is a label (#548): the chosen store location's nickname,
    // posted only when one is chosen on a store that asks for it. It has
    // no effect on where the order ships or what it is charged.
    // Returns null on failure (reported).
    async prepareCheckoutFields() {
      const checkoutFieldsValues = this.getCheckoutFieldsValues || {};
      const data = Object.assign({}, checkoutFieldsValues);
      const location = data.Location;
      delete data.Location;
      if (
        typeof location === "string" &&
        location &&
        location !== NO_LOCATION_OPTION &&
        (this.campaign.checkout_fields || []).includes("Location")
      ) {
        data.Location = location;
      }
      if (storeResolvesDestination(this.campaign)) {
        if (this.campaign.checkout_fields.includes("Shopper Location")) {
          data["Shopper Location"] = "";
        }
      } else {
        data["Shopper Location"] = shopperLocationField(this.getShopperLocation);
      }
      return data;
    },

    // The money args every checkout call posts -- pre-create, finalize and
    // single-shot alike -- all from the server quote (see addOrder). The
    // quote's card charge is 0 on a store that takes no card. Every call
    // names the store the order is for, and a back-office order is placed
    // ON BEHALF of the buyer (the server honors it only for a caller with
    // `shopper.act_as`).
    checkoutArgs(quote, checkoutFields) {
      return {
        ...cartStoreArgs(this.storeID),
        placed_on_behalf: true,
        // Required by an N-department store (the server 409s without it).
        client_department_id: this.clientDepartmentId ?? null,
        checkout_fields: checkoutFields,
        cart_sales_tax: quote.sales_tax,
        cart_processing_fee: quote.processing_fee,
        cart_charge: quote.card_charge,
        flat_rate: 0,
        bigger_than_zero: quote.card_charge > 0,
        sales_tax: quote.sales_tax,
        // The SERVER's tax lookup for this order (checkout_quote).
        tax_info: this.campaign.collect_sales_tax
          ? quote.tax_info || null
          : null,
        coupon_code: this.getShopperCoupon,
        shipping_cost: quote.shipping_cost,
        // The server quote behind a live rate: on a live-rate store the
        // server charges what it stored for this id (409 without one).
        shipping_quote_id: this.getShopperShippingQuoteId,
        // Honest about the quote: false only when the cart is showing
        // "rate unavailable" on a store that charges for shipping.
        shipping_rate_available: this.campaign.shipping_cost_enabled
          ? this.getShopperShippingAvailable !== false
          : true,
        used_allowance: quote.allowance_draw,
        // The ITEMS subtotal, which is what the server compares.
        cart_total: quote.cart_total,
        company_allowance: quote.allowance_draw,
        sub_total: quote.cart_total,
        app_fee: quote.app_fee,
        buyer_location: this.buyerLocation(),
      };
    },

    // POST `checkout` (single-shot, or the finalizer when `order_id` is in
    // `args`). Returns the order id, or null on failure (reported).
    async submitCheckout(args) {
      let response;
      try {
        response = await this.$axios.post(
          `/accounts/${this.$store.state.account._jv.id}/checkout`,
          { meta: { method: "checkout", args } }
        );
      } catch (err) {
        this.$store.dispatch("raiseError", err);
        return null;
      }
      const orderID = response.data.meta.result.id
        ? response.data.meta.result.id
        : null;
      this.$store.commit("setNewOrderAdded", true);
      this.showNewOrderDialog();
      return orderID;
    },

    // No card: the legacy single-shot order (staff are trusted -- an order on
    // a card store placed this way is not charged).
    async placeOrder(quote) {
      const checkoutFields = await this.prepareCheckoutFields();
      if (!checkoutFields) {
        return null;
      }
      return this.submitCheckout(this.checkoutArgs(quote, checkoutFields));
    },
  },
};
</script>

<style scoped>
.add-order-component {
  padding: 8px;
}
</style>

